import { Feather } from '@expo/vector-icons';
import { File, Paths } from 'expo-file-system';
import * as FileSystem from 'expo-file-system/legacy';
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Modal, ModalProps, Platform } from 'react-native';
import styled from 'styled-components/native';
import * as Sharing from 'expo-sharing';
import PDFReader from 'rn-pdf-reader-js';

import api from '../api/api';
import Colors from '../constants/Colors';
import AlertBox from './AlertBox';
import IconButton from './IconButton';

const StyledCloseIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'web' ? 20 : 48}px;
  left: 20px;
  z-index: 1;
`;

const StyledShareIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'web' ? 20 : 48}px;
  right: 20px;
  z-index: 1;
`;

const StyledToolbarView = styled.View<{ loadingPreview: boolean }>`
  ${p =>
    p.loadingPreview
      ? `
  height: 100%;
  align-items: center;
  justify-content: center;`
      : `height: 96px;`}
`;

const StyledWebView = styled(PDFReader)`
  width: 100%;
  flex: 1;
`;

class DownloadCancelledError extends Error {
  constructor() {
    super('Download cancelled');
    this.name = 'DownloadCancelledError';
  }
}

async function isValidPdfFile(fileUri: string): Promise<boolean> {
  try {
    const info = await FileSystem.getInfoAsync(fileUri);
    if (!info.exists) {
      return false;
    }
    if (info.size < 4) {
      return false;
    }
    const base64Prefix = await FileSystem.readAsStringAsync(fileUri, {
      encoding: FileSystem.EncodingType.Base64,
      length: 4,
      position: 0
    });
    const prefix = atob(base64Prefix);
    return prefix === '%PDF';
  } catch {
    return false;
  }
}

async function downloadPdfForPreview({
  url,
  cacheFileName,
  headers,
  useCacheIfValid = false,
  onRegisterCancel
}: {
  url: string;
  cacheFileName: string;
  headers?: Record<string, string>;
  useCacheIfValid?: boolean;
  onRegisterCancel: (cancel: () => Promise<void>) => void;
}): Promise<{ uri: string; base64DataUri: string }> {
  const destinationFile = new File(Paths.cache, cacheFileName);
  const fileUri = destinationFile.uri;

  if (useCacheIfValid && (await isValidPdfFile(fileUri))) {
    const base64 = await FileSystem.readAsStringAsync(fileUri, {
      encoding: FileSystem.EncodingType.Base64
    });
    onRegisterCancel(async () => {});
    return {
      uri: fileUri,
      base64DataUri: `data:application/pdf;base64,${base64}`
    };
  }

  if (useCacheIfValid) {
    const info = await FileSystem.getInfoAsync(fileUri);
    if (info.exists) {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    }
  }

  const download = FileSystem.createDownloadResumable(url, fileUri, {
    headers
  });

  let downloadCompleted = false;

  onRegisterCancel(async () => {
    try {
      await download.cancelAsync();
    } catch {
      // Download may already be finished or cancelled
    }
    if (!downloadCompleted) {
      try {
        await FileSystem.deleteAsync(fileUri, { idempotent: true });
      } catch {
        // Best-effort removal of a partial file
      }
    }
  });

  const result = await download.downloadAsync();
  if (!result) {
    throw new DownloadCancelledError();
  }

  if (!(await isValidPdfFile(fileUri))) {
    await FileSystem.deleteAsync(fileUri, { idempotent: true });
    throw new Error('Downloaded file is not a valid PDF');
  }

  downloadCompleted = true;

  const base64 = await FileSystem.readAsStringAsync(fileUri, {
    encoding: FileSystem.EncodingType.Base64
  });

  return {
    uri: fileUri,
    base64DataUri: `data:application/pdf;base64,${base64}`
  };
}

const PdfModal: React.FC<
  ModalProps & {
    title?: string;
    url: string;
    headers?: any;
    cacheKey?: string;
    idempotentDownload?: boolean;
  }
> = ({
  title,
  url,
  headers,
  cacheKey,
  idempotentDownload = true,
  ...props
}) => {
  // convert whitespaces to %20 as ios FileSystem does not allow whitespaces
  const pdfUrl = url.replaceAll(' ', '%20');
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [localUri, setLocalUri] = useState<string>('');
  const [base64PDF, setBase64PDF] = useState<string>();
  const [error, setError] = useState<string>();
  const [replaceInitialSource, setReplaceInitialSource] = useState(false);
  const headersRef = useRef(headers);
  headersRef.current = headers;

  useEffect(() => {
    let cancelled = false;
    let webPdfObjectUrl: string | undefined;
    setLoadingPreview(true);
    setError(undefined);
    setBase64PDF(undefined);
    setLocalUri('');
    setReplaceInitialSource(false);

    let cancelDownload: (() => Promise<void>) | undefined;

    (async () => {
      try {
        if (Platform.OS !== 'web') {
          const cacheFileName = cacheKey
            ? `${encodeURIComponent(title || 'document')}-${encodeURIComponent(cacheKey)}.pdf`
            : `${encodeURIComponent(title || 'document')}.pdf`;
          const { uri, base64DataUri } = await downloadPdfForPreview({
            url: pdfUrl,
            cacheFileName,
            headers: headersRef.current as Record<string, string> | undefined,
            useCacheIfValid: idempotentDownload,
            onRegisterCancel: cancel => {
              cancelDownload = cancel;
            }
          });
          if (cancelled) {
            return;
          }
          setLocalUri(uri);
          setBase64PDF(base64DataUri);
          setLoadingPreview(false);
        } else {
          const result = await api.get(pdfUrl, {}).response;
          const blob = await result.blob();
          if (cancelled) {
            return;
          }
          webPdfObjectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(webPdfObjectUrl);
            return;
          }
          const iframe: HTMLIFrameElement | null = document.getElementById(
            'iframePdf'
          ) as HTMLIFrameElement;
          iframe.src = webPdfObjectUrl;
          setLoadingPreview(false);
        }
      } catch (downloadError) {
        if (!cancelled && !(downloadError instanceof DownloadCancelledError)) {
          setError((downloadError as Error).message);
          setLoadingPreview(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      void cancelDownload?.();
      if (webPdfObjectUrl) {
        URL.revokeObjectURL(webPdfObjectUrl);
      }
    };
  }, [pdfUrl, title, cacheKey, idempotentDownload]);

  const { onRequestClose } = props;
  const [loading, setLoading] = useState(false);

  const onShare = async () => {
    setLoading(true);
    await Sharing.shareAsync(localUri).catch(err =>
      setError((err as Error).message)
    );
    setLoading(false);
  };

  return (
    <Modal animationType='slide' {...props}>
      <StyledToolbarView loadingPreview={loadingPreview}>
        <StyledCloseIconButton onPress={e => onRequestClose?.(e)}>
          <Feather name='x' size={28} color='black' />
        </StyledCloseIconButton>
        {Platform.OS !== 'web' && !loadingPreview && !error && (
          <StyledShareIconButton onPress={!loading ? onShare : undefined}>
            {loading ? (
              <ActivityIndicator color={Colors.primary} />
            ) : (
              <Feather name='share' size={28} color='black' />
            )}
          </StyledShareIconButton>
        )}
        {loadingPreview && (
          <ActivityIndicator size='large' color={Colors.primary} />
        )}
      </StyledToolbarView>
      {error ? (
        <AlertBox fullWidth severity='error'>
          {error}
        </AlertBox>
      ) : Platform.OS === 'web' ? (
        <iframe id='iframePdf' style={{ border: 0, flex: 1 }}></iframe>
      ) : (
        !!base64PDF && (
          <StyledWebView
            source={{
              base64: base64PDF
            }}
            webviewProps={
              replaceInitialSource ? {} : { source: { html: '<div/>' } }
            }
            onLoad={() => {
              setReplaceInitialSource(true);
            }}
            //removed zoom buttons as it is crashing the pdf enabled pinch zoom instead
            customStyle={{
              readerContainerZoomContainer: { width: 0, height: 0 }
            }}
            withPinchZoom
            withScroll
            noLoader
          />
        )
      )}
    </Modal>
  );
};

export default PdfModal;
